<style lang="scss" scoped>
/deep/ .comp-employee-import-dialog {
    max-width: 960px;
}
.comp-employee-import {
    .import-upload {
        margin-top: 15px;
    }
    .report-stats {
        display: flex;
        flex-wrap: wrap;
        margin-bottom: 10px;
    }
    .report-stat {
        min-width: 90px;
        margin-right: 10px;
        padding: 6px 12px;
        background: #f5f7fa;
        border-radius: 4px;
        text-align: center;
        b {
            display: block;
            font-size: 18px;
            color: #303133;
        }
        span {
            font-size: 12px;
            color: #909399;
        }
    }
    .report-table {
        width: 100%;
    }
    .empty-tip {
        padding: 20px 0;
        text-align: center;
        color: #909399;
    }
    .sync-form {
        margin-top: 15px;
    }
    .sync-tip {
        margin-left: 10px;
        color: #909399;
        font-size: 12px;
    }
    .sync-scope {
        margin-bottom: 10px;
        color: #606266;
    }
    .result-summary {
        margin-bottom: 10px;
    }
    .flow-switch {
        margin-bottom: 8px;
    }
}
</style>

<template>
    <div class="comp-employee-import">
        <el-dialog :title="dialogTitle" custom-class="c-el-dialog comp-employee-import-dialog"
            :visible.sync="showDialog" :close-on-click-modal="false" @closed="reset">
            <div v-if="!result" v-loading="syncing" element-loading-text="正在处理数据，数据量大时可能需要几分钟，请稍候…">
                <el-radio-group v-if="!isArchive" v-model="flowType" class="flow-switch">
                    <el-radio-button label="hire">入职数据</el-radio-button>
                    <el-radio-button label="leave">离职数据</el-radio-button>
                </el-radio-group>

                <el-alert type="info" :closable="false" show-icon>
                    <template v-if="isArchive">
                        <div>档案导入：把「导出档案」下载的表格修改后直接回导，与钉钉入职/离职导入互不相干。</div>
                        <div>按身份证号匹配：已有档案仅更新 姓名/手机号/运营区域/职位/用工类型/职业技能/银行卡号/开户行/合同到期时间/备注（空单元格保留原值），更新结果逐字段显示修改内容；不改已有档案的状态与入离职日期（请走入职/离职流程）。</div>
                        <div>档案不存在的身份证号会自动新增建档：须有姓名和手机号，入职日期取「入职日期」列（缺省按导入当天），带「最后工作日」的行同时写离职记录。</div>
                    </template>
                    <template v-else-if="flowType === 'hire'">
                        <div>同步或导入入职审批，系统按身份证号建立员工档案；入职与离职数据可独立操作，互不阻塞。</div>
                        <div>仅导入「已完成 + 审批通过」的审批；审批中的下次同步会自动补上；同一审批重复同步不会重复落库。</div>
                    </template>
                    <template v-else>
                        <div>离职审批按身份证号匹配员工档案，可独立同步，无需先处理入职数据。</div>
                        <div>未匹配到档案的离职行会失败（提示先补该员工入职数据），补齐入职后重新同步即可。</div>
                    </template>
                </el-alert>

                <el-form v-if="isApi" class="sync-form" label-width="120px" @submit.native.prevent>
                    <el-form-item label="审批创建时间">
                        <el-date-picker v-model="syncRange" type="daterange" range-separator="至"
                            start-placeholder="开始日期" end-placeholder="结束日期" size="medium"
                            value-format="yyyy-MM-dd" style="width: 320px">
                        </el-date-picker>
                    </el-form-item>
                    <el-form-item>
                        <el-button type="primary" :loading="syncing" @click="syncDingtalk">
                            <i class="fa fa-refresh"></i> {{ flowType === 'hire' ? '同步入职' : '同步离职' }}
                        </el-button>
                        <span class="sync-tip">默认最近 7 天；单次最多 365 天，更早的数据请用「钉钉数据 → 上传」上传 Excel。</span>
                    </el-form-item>
                </el-form>
                <el-upload v-else class="import-upload" drag :action="uploadUrl" :headers="uploadHeaders"
                    :data="uploadData" :show-file-list="false" :before-upload="beforeUpload"
                    :on-success="onSuccess" :on-error="onError" accept=".xlsx,.xlsm">
                    <i class="el-icon-upload"></i>
                    <div class="el-upload__text" v-if="isArchive">
                        将员工档案 Excel 拖到此处，或<em>点击选择文件</em>
                    </div>
                    <div class="el-upload__text" v-else>
                        将{{ flowType === 'hire' ? '入职' : '离职' }}审批 Excel 拖到此处，或<em>点击选择文件</em>
                    </div>
                    <div class="el-upload__tip" slot="tip" v-if="isArchive">
                        仅支持 .xlsx / .xlsm；「导出档案」下载的员工档案信息表可直接修改后上传
                    </div>
                    <div class="el-upload__tip" slot="tip" v-else>
                        仅支持 .xlsx / .xlsm，文件内容需与当前选择的类型一致（{{ flowType === 'hire' ? '入职' : '离职' }}）
                    </div>
                </el-upload>
            </div>

            <div v-else-if="isArchive">
                <div class="sync-scope"><b>档案导入结果</b></div>
                <el-alert class="result-summary" :type="archiveResultSummary.type" :closable="false" show-icon
                    :title="archiveResultSummary.title" :description="archiveResultSummary.description" />
                <div class="report-stats">
                    <div class="report-stat"><b>{{ result.totalCount }}</b><span>总数</span></div>
                    <div class="report-stat"><b>{{ result.addedCount }}</b><span>新增</span></div>
                    <div class="report-stat"><b>{{ result.updatedCount }}</b><span>更新</span></div>
                    <div class="report-stat"><b>{{ result.unchangedCount }}</b><span>无修改</span></div>
                    <div class="report-stat"><b>{{ result.skippedCount }}</b><span>跳过</span></div>
                    <div class="report-stat"><b>{{ result.failedCount }}</b><span>失败</span></div>
                </div>

                <el-tabs v-model="activeTab">
                    <el-tab-pane :label="`更新 (${result.updatedCount})`" name="detail">
                        <el-table class="report-table" :data="result.updated" border size="small"
                            max-height="360" v-if="result.updated.length">
                            <el-table-column label="行号" prop="rowNumber" width="80"></el-table-column>
                            <el-table-column label="姓名" prop="name" width="100"></el-table-column>
                            <el-table-column label="身份证号" prop="idNumber" min-width="160"></el-table-column>
                            <el-table-column label="修改内容" min-width="320" show-overflow-tooltip>
                                <template slot-scope="scope">{{ formatChanges(scope.row) }}</template>
                            </el-table-column>
                        </el-table>
                        <div class="empty-tip" v-if="!result.updated.length">没有更新的数据</div>
                    </el-tab-pane>

                    <el-tab-pane :label="`新增 (${result.addedCount})`" name="added">
                        <el-table class="report-table" :data="result.added" border size="small"
                            max-height="360" v-if="result.added.length">
                            <el-table-column label="行号" prop="rowNumber" width="80"></el-table-column>
                            <el-table-column label="姓名" prop="name" width="100"></el-table-column>
                            <el-table-column label="身份证号" prop="idNumber" min-width="170"></el-table-column>
                            <el-table-column label="入职日期" width="110">
                                <template slot-scope="scope">{{ formatDate(scope.row.effectiveDate) }}</template>
                            </el-table-column>
                        </el-table>
                        <div class="empty-tip" v-if="!result.added.length">没有新增的数据</div>
                    </el-tab-pane>

                    <el-tab-pane :label="`跳过 (${result.skippedCount})`" name="skipped">
                        <el-alert type="info" :closable="false" show-icon class="result-summary"
                            title="身份证号为空、文件内重复或缺少姓名/手机号而无法建档的行会进入这里。" />
                        <el-table class="report-table" :data="result.skipped" border size="small" max-height="360">
                            <el-table-column label="行号" prop="rowNumber" width="80"></el-table-column>
                            <el-table-column label="姓名" prop="name" width="100"></el-table-column>
                            <el-table-column label="身份证号" prop="idNumber" min-width="170"></el-table-column>
                            <el-table-column label="原因" prop="reason" min-width="280" show-overflow-tooltip></el-table-column>
                        </el-table>
                        <div class="empty-tip" v-if="!result.skipped.length">没有跳过数据</div>
                    </el-tab-pane>

                    <el-tab-pane :label="`失败 (${result.failedCount})`" name="failed">
                        <el-table class="report-table" :data="result.failed" border size="small" max-height="360">
                            <el-table-column label="行号" prop="rowNumber" width="80"></el-table-column>
                            <el-table-column label="姓名" prop="name" width="100"></el-table-column>
                            <el-table-column label="身份证号" prop="idNumber" min-width="170"></el-table-column>
                            <el-table-column label="原因" prop="reason" min-width="260" show-overflow-tooltip></el-table-column>
                        </el-table>
                        <div class="empty-tip" v-if="!result.failed.length">没有失败数据</div>
                    </el-tab-pane>
                </el-tabs>
            </div>

            <div v-else>
                <div class="sync-scope">
                    <b>{{ resultSourceDesc }} · {{ result.typeDesc }}结果</b>
                    <template v-if="result.startDate">
                        ：{{ formatDate(result.startDate) }} ~ {{ formatDate(result.endDate) }}
                    </template>
                    <span v-if="result.instanceCount">，审批实例 {{ result.instanceCount }} 条</span>
                </div>
                <el-alert class="result-summary" :type="resultSummary.type" :closable="false" show-icon
                    :title="resultSummary.title" :description="resultSummary.description" />
                <div class="report-stats">
                    <div class="report-stat"><b>{{ processedCount }}</b><span>已处理</span></div>
                    <div class="report-stat"><b>{{ result.failedCount }}</b><span>失败</span></div>
                    <div class="report-stat"><b>{{ result.skippedCount }}</b><span>跳过</span></div>
                    <div class="report-stat"><b>{{ result.pendingCount }}</b><span>待确认</span></div>
                </div>

                <el-tabs v-model="activeTab">
                    <el-tab-pane :label="`已处理 (${processedCount})`" name="detail">
                        <el-table class="report-table" :data="processedDetails" border size="small"
                            max-height="360" v-if="processedDetails.length">
                            <el-table-column label="类型" width="80" align="center">
                                <template slot-scope="scope">
                                    <el-tag size="mini" type="success" v-if="scope.row.recordType === '入职'">入职</el-tag>
                                    <el-tag size="mini" type="info" v-else>离职</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="姓名" prop="name" width="100"></el-table-column>
                            <el-table-column label="身份证号" prop="idNumber" min-width="170"></el-table-column>
                            <el-table-column label="审批编号" prop="sourceKey" min-width="180"
                                show-overflow-tooltip></el-table-column>
                            <el-table-column label="生效日期" width="110">
                                <template slot-scope="scope">{{ formatDate(scope.row.effectiveDate) }}</template>
                            </el-table-column>
                        </el-table>
                        <div class="empty-tip" v-if="!processedDetails.length">没有成功处理的数据</div>
                    </el-tab-pane>

                    <el-tab-pane :label="`失败 (${result.failedCount})`" name="failed">
                        <el-table class="report-table" :data="result.failed" border size="small" max-height="360">
                            <el-table-column label="姓名" prop="name" width="100"></el-table-column>
                            <el-table-column label="身份证号" prop="idNumber" min-width="170"></el-table-column>
                            <el-table-column label="审批编号" prop="sourceKey" min-width="180"
                                show-overflow-tooltip></el-table-column>
                            <el-table-column label="原因" prop="reason" min-width="260" show-overflow-tooltip></el-table-column>
                        </el-table>
                        <div class="empty-tip" v-if="!result.failed.length">没有失败数据</div>
                    </el-tab-pane>

                    <el-tab-pane :label="`跳过 (${result.skippedCount})`" name="skipped">
                        <el-alert type="info" :closable="false" show-icon class="result-summary"
                            title="审批中、重复导入、当前在职或已离职等记录会进入这里，不一定是异常。" />
                        <el-table class="report-table" :data="result.skipped" border size="small" max-height="360">
                            <el-table-column label="姓名" prop="name" width="100"></el-table-column>
                            <el-table-column label="身份证号" prop="idNumber" min-width="170"></el-table-column>
                            <el-table-column label="审批编号" prop="sourceKey" min-width="180"
                                show-overflow-tooltip></el-table-column>
                            <el-table-column label="原因" prop="reason" min-width="280" show-overflow-tooltip></el-table-column>
                        </el-table>
                        <div class="empty-tip" v-if="!result.skipped.length">没有跳过数据</div>
                    </el-tab-pane>

                    <el-tab-pane :label="`待确认 (${result.pendingCount})`" name="pending">
                        <el-alert type="warning" :closable="false" show-icon
                            v-if="result.pending.length">
                            历史离职单没有身份证号，按姓名匹配到多名员工，请选择正确的档案后确认。
                        </el-alert>
                        <el-table class="report-table" :data="result.pending" border size="small" max-height="360">
                            <el-table-column label="姓名" prop="name" width="100"></el-table-column>
                            <el-table-column label="审批编号" prop="sourceKey" min-width="180"
                                show-overflow-tooltip></el-table-column>
                            <el-table-column label="最后工作日" width="110">
                                <template slot-scope="scope">{{ formatDate(scope.row.effectiveDate) }}</template>
                            </el-table-column>
                            <el-table-column label="选择档案" min-width="300">
                                <template slot-scope="scope">
                                    <el-select v-model="pendingSelection[scope.row.rowNumber]" size="small"
                                        placeholder="请选择同名员工档案" style="width: 100%">
                                        <el-option v-for="item in scope.row.candidates" :key="item.id"
                                            :label="candidateLabel(item)" :value="item.idNumber"></el-option>
                                    </el-select>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" width="110" align="center">
                                <template slot-scope="scope">
                                    <el-button type="primary" plain size="mini" :loading="confirmingRow === scope.row.rowNumber"
                                        @click="confirmPending(scope.row)">确认离职</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                        <div class="empty-tip" v-if="!result.pending.length">没有待确认数据</div>
                    </el-tab-pane>
                </el-tabs>
            </div>

            <div slot="footer">
                <el-button @click="showDialog = false">关闭</el-button>
                <el-button type="primary" @click="continueCurrent" v-if="result">
                    {{ isApi ? '继续同步' : '继续导入' }}
                </el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import * as funEmployee from "@/api/employees"
import moment from "moment"

export default {
    name: "compEmployeeImport",
    data() {
        return {
            showDialog: false,
            uploadHeaders: window.$common.appHeaders(),
            // 弹框来源：api = 钉钉同步按钮，excel = 钉钉导入按钮，archive = 导入档案按钮
            mode: "api",
            flowType: "hire",
            syncRange: [],
            syncing: false,
            result: null,
            activeTab: "detail",
            pendingSelection: {},
            confirmingRow: null
        }
    },
    computed: {
        isApi() {
            return this.mode === "api"
        },
        isArchive() {
            return this.mode === "archive"
        },
        dialogTitle() {
            if (this.isArchive) return "员工档案导入"
            return this.isApi ? "钉钉员工同步" : "钉钉员工导入"
        },
        uploadUrl() {
            const base = window.$common.appBaseApi()
            return this.isArchive
                ? base + "/api/app/employee-archive/import-archive"
                : base + "/api/app/employee-archive/import"
        },
        uploadData() {
            if (this.isArchive) return {}
            return { type: this.flowType === "hire" ? "Hire" : "Leave" }
        },
        // 档案导入结果汇总
        archiveResultSummary() {
            if (!this.result) {
                return { type: "info", title: "", description: "" }
            }
            const r = this.result
            const issues = []
            if (r.failedCount) issues.push(`失败 ${r.failedCount} 行`)
            if (r.skippedCount) issues.push(`跳过 ${r.skippedCount} 行`)
            const type = r.failedCount ? "error" : r.skippedCount ? "warning" : "success"
            const tips = []
            if (r.skippedCount) tips.push("跳过行多为缺少姓名/手机号或文件内身份证号重复")
            if (r.failedCount) tips.push("失败行请查看原因后重试")
            const done = []
            if (r.addedCount) done.push(`新增档案 ${r.addedCount} 条`)
            if (r.updatedCount || r.unchangedCount) done.push(`更新 ${r.updatedCount} 条、无修改 ${r.unchangedCount} 条`)
            return {
                type,
                title: issues.length ? `处理完成：${issues.join("、")}` : "处理完成，全部数据已落库",
                description: [
                    done.length ? `已完成：${done.join("、")}。` : "",
                    tips.length ? tips.join("；") + "。" : ""
                ].join("")
            }
        },
        resultSourceDesc() {
            // API 同步会带上日期范围，Excel 补录没有
            return this.result && this.result.startDate ? "钉钉同步" : "钉钉导入"
        },
        resultSummary() {
            if (!this.result) {
                return { type: "info", title: "", description: "" }
            }
            let done = []
            if (this.result.createdArchiveCount) done.push(`新增档案 ${this.result.createdArchiveCount} 条`)
            if (this.result.updatedArchiveCount) done.push(`更新档案 ${this.result.updatedArchiveCount} 条`)
            if (this.result.hireRecordCount) done.push(`入职记录 ${this.result.hireRecordCount} 条`)
            if (this.result.leaveRecordCount) done.push(`离职记录 ${this.result.leaveRecordCount} 条`)

            let issues = []
            if (this.result.failedCount) issues.push(`失败 ${this.result.failedCount} 行`)
            if (this.result.pendingCount) issues.push(`待确认 ${this.result.pendingCount} 行`)
            if (this.result.skippedCount) issues.push(`跳过 ${this.result.skippedCount} 行`)

            let type = "success"
            if (this.result.failedCount) type = "error"
            else if (this.result.pendingCount) type = "warning"
            else if (this.result.skippedCount) type = "info"

            let tips = []
            if (this.result.failedCount) tips.push("失败行请查看原因后重试")
            if (this.result.pendingCount) tips.push("待确认行请选择正确档案")
            if (this.result.skippedCount) tips.push("跳过行多为审批中、重复导入或已离职")

            let hasDone = this.result.createdArchiveCount || this.result.updatedArchiveCount
                || this.result.hireRecordCount || this.result.leaveRecordCount
            return {
                type,
                title: issues.length
                    ? `处理完成：${issues.join("、")}`
                    : (hasDone ? "处理完成，全部数据已落库" : "处理完成，没有可落库的数据"),
                description: [done.length ? `已完成：${done.join("、")}。` : "", tips.length ? tips.join("；") + "。" : ""].join("")
            }
        },
        processedDetails() {
            if (!this.result) return []
            return [
                ...(this.result.hireRecords || []).map(t => ({ ...t, recordType: "入职" })),
                ...(this.result.leaveRecords || []).map(t => ({ ...t, recordType: "离职" }))
            ]
        },
        processedCount() {
            return this.processedDetails.length
        }
    },
    methods: {
        // open("api") 钉钉同步，open("excel") 钉钉导入，open("archive") 导入档案
        open(mode = "api") {
            this.reset()
            this.mode = mode
            this.showDialog = true
        },
        reset() {
            this.result = null
            this.flowType = "hire"
            this.mode = "api"
            this.syncRange = [
                moment().subtract(6, "days").format("YYYY-MM-DD"),
                moment().format("YYYY-MM-DD")
            ]
            this.syncing = false
            this.activeTab = "detail"
            this.pendingSelection = {}
            this.confirmingRow = null
        },
        // 钉钉 API 按日期范围同步
        syncDingtalk() {
            if (!this.syncRange || this.syncRange.length !== 2) {
                this.$message.warning("请选择审批创建时间范围。")
                return
            }
            let start = moment(this.syncRange[0])
            let end = moment(this.syncRange[1])
            if (end.diff(start, "days") > 364) {
                this.$message.error("单次最多同步 365 天，请分批同步。")
                return
            }
            this.syncing = true
            funEmployee.SyncEmployeeArchiveDingtalk({
                type: this.flowType === "hire" ? 1 : 2,
                startDate: this.syncRange[0],
                endDate: this.syncRange[1]
            }).then(response => {
                this.syncing = false
                this.showResult(response)
            }).catch(() => {
                this.syncing = false
            })
        },
        // 保留当前类型与日期范围，只清空结果继续下一批
        continueCurrent() {
            this.result = null
            this.activeTab = "detail"
            this.pendingSelection = {}
            this.confirmingRow = null
        },
        beforeUpload(file) {
            let name = (file.name || "").toLowerCase()
            if (!name.endsWith(".xlsx") && !name.endsWith(".xlsm")) {
                this.$message.error(this.isArchive
                    ? "请上传 .xlsx / .xlsm 文件（不支持旧版 .xls）。"
                    : "请上传钉钉导出的 .xlsx 文件（不支持旧版 .xls）。")
                return false
            }
            this.syncing = true
            return true
        },
        onSuccess(response) {
            this.syncing = false
            this.showResult(response)
        },
        showResult(response) {
            if (!response || response.error) {
                this.$message.error((response && response.error && response.error.message) || "同步失败，请重试！")
                return
            }
            this.result = response
            // 结果页统一先展示处理明细，失败/跳过/待确认由汇总提示引导查看
            this.activeTab = "detail"
            let pending = response.pending || []
            pending.forEach(t => {
                this.$set(this.pendingSelection, t.rowNumber, "")
            })
            if (response.createdArchiveCount || response.updatedArchiveCount
                || response.hireRecordCount || response.leaveRecordCount
                || (this.isArchive && (response.addedCount || response.updatedCount))) {
                this.$emit("refreshData")
            }
        },
        onError(error) {
            this.syncing = false
            let message = "导入失败，请重试！"
            try {
                let data = JSON.parse(error.message)
                message = (data.error && data.error.message) || data.message || message
            } catch (e) {
                if (error && error.message) message = error.message
            }
            this.$message.error(message)
        },
        candidateLabel(item) {
            let hire = item.hireDate ? `入职 ${this.formatDate(item.hireDate)}` : "无入职日期"
            return `${item.name} / ${item.idNumber} / ${item.statusDesc} / ${hire}`
        },
        // 档案导入的逐字段修改内容：字段：原值 → 新值；…
        formatChanges(row) {
            return (row.changes || []).map(t => `${t.field}：${t.before || "空"} → ${t.after || "空"}`).join("；")
        },
        formatDate(value) {
            return value ? moment(value).format("YYYY-MM-DD") : ""
        },
        confirmPending(row) {
            let idNumber = this.pendingSelection[row.rowNumber]
            if (!idNumber) {
                this.$message.warning("请先选择要确认的员工档案。")
                return
            }
            this.confirmingRow = row.rowNumber
            funEmployee.ConfirmEmployeeArchiveImport({
                idNumber: idNumber,
                name: row.name,
                effectiveDate: row.effectiveDate,
                source: row.source,
                sourceKey: row.sourceKey,
                rawSnapshot: row.rawSnapshot,
                remark: row.remark
            }).then(detail => {
                this.result.pending = this.result.pending.filter(t => t.rowNumber !== row.rowNumber)
                this.result.pendingCount = this.result.pending.length
                if (detail) {
                    detail.rowNumber = row.rowNumber
                    this.result.leaveRecords.push(detail)
                }
                this.result.leaveRecordCount++
                this.$message.success("已确认离职。")
                this.$emit("refreshData")
            }).finally(() => {
                this.confirmingRow = null
            })
        }
    }
}
</script>
